import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "react-query";
import MaterialReactTable from "material-react-table";
import {
  Spinner,
  Toast,
  ToastBody,
  ToastHeader,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
} from "reactstrap";
import useAxios from "../../sections/useAxios";
import { toast } from "react-toastify";
import InvestorEditForm from "./components/investor-edit-form-modal";
import ConfirmDialog from "../../components/common/confirmation-dialog.jsx";

const InvestorsListing = () => {
  const api = useAxios();
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState("investor"); // "investor" or "registration"

  const {
    data,
    isLoading: investorsLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ["investors-list"],
    async () => {
      const res = await api.get(`company/all-available-investors`);
      return res.data;
    },
    { cacheTime: 0 }
  );

  const investors = data ?? [];

  // Delete Investor / Registration Mutation
  const deleteMutation = useMutation(
    async ({ id, type }) => {
      if (type === "investor") {
        return api.delete(`company/update-investor/${id}`);
      }
      return api.delete(`company/admin-cancel-registration/${id}`);
    },
    {
      onSuccess: (_, variables) => {
        if (variables.type === "investor") {
          toast.success("Investor deleted successfully");
          refetch();
        } else {
          toast.success("Registration deleted successfully");
          // remove from modal state
          setSelectedInvestor((prev) => ({
            ...prev,
            registrations: prev.registrations.filter(
              (r) => r.id !== variables.id
            ),
          }));
        }
        setConfirmDialogOpen(false);
      },
      onError: () => {
        toast.error("Error deleting record");
        setConfirmDialogOpen(false);
      },
    }
  );

  useEffect(() => {
    if (isError) {
      setToastMsg(error.message || "Something went wrong");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
  }, [isError, error]);

  const columns = [
    { accessorKey: "id", header: "ID", size: 50 },
    {
      accessorKey: "name",
      header: "Investor Name",
    },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "company", header: "Company" },
    {
      accessorKey: "registration_count",
      header: "Registration Count",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "registrations",
      header: "Registrations",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <i
          className="fa-solid fa-clipboard-list fa-xl text-primary"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedInvestor(row.original);
            setModalOpen(true);
          }}
        ></i>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString(),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <div className="d-flex gap-3 justify-content-center">
          <i
            className="fa-solid fa-pen text-success pointer"
            onClick={() => {
              setSelectedInvestor(row.original);
              setEditModalOpen(true);
            }}
          ></i>
          <i
            className="fa-solid fa-trash text-danger pointer"
            onClick={() => {
              setDeleteId(row.original.id);
              setDeleteType("investor");
              setConfirmDialogOpen(true);
            }}
          ></i>
        </div>
      ),
    },
  ];

  return (
    <div className="container mt-4">
      <h4 className="primary-color fw-bold">Investor's Listing</h4>

      {investorsLoading && (
        <div className="d-flex justify-content-center align-items-center my-4">
          <Spinner color="primary" />
        </div>
      )}

      {showToast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 1050,
            minWidth: "250px",
          }}
        >
          <Toast isOpen={showToast}>
            <ToastHeader icon="danger">Error</ToastHeader>
            <ToastBody>{toastMsg}</ToastBody>
          </Toast>
        </div>
      )}

      {!investorsLoading && !isError && (
        <MaterialReactTable
          columns={columns}
          data={investors}
          muiTablePaperProps={{
            elevation: 10,
          }}
        />
      )}

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Registrations for {selectedInvestor?.name}
        </ModalHeader>
        <ModalBody>
          {selectedInvestor?.registrations?.length > 0 ? (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Meeting</th>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvestor.registrations.map((reg) => (
                  <tr key={reg.id}>
                    <td>{reg.id}</td>
                    <td>{reg.meeting}</td>
                    <td>{reg.company}</td>
                    <td>{reg.location}</td>
                    <td>{reg.start_time}</td>
                    <td>{reg.end_time}</td>
                    <td className="text-center">
                      <i
                        className="fa-solid fa-trash text-danger pointer"
                        onClick={() => {
                          setDeleteId(reg.id);
                          setDeleteType("registration");
                          setConfirmDialogOpen(true);
                        }}
                      ></i>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No registrations found for this investor.</p>
          )}
        </ModalBody>
      </Modal>

      <InvestorEditForm
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(!editModalOpen)}
        investor={selectedInvestor}
        refetch={refetch}
      />

      <ConfirmDialog
        isOpen={confirmDialogOpen}
        toggle={() => setConfirmDialogOpen(!confirmDialogOpen)}
        title="Confirm Delete"
        message={`Are you sure you want to delete this ${deleteType === "investor" ? "investor" : "registration"
          }?`}
        onConfirm={() => deleteMutation.mutate({ id: deleteId, type: deleteType })}
        loading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default InvestorsListing;
